import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import SoftChip from '../../../components/dashboard/SoftChip';
import { formatDate, formatTaxId } from '../../../utils/formatters';

const STATUS_COLOR = { active: 'success', inactive: 'default', deleted: 'error' };

export const customerStatus = (customer) => {
  if (customer.is_deleted) return 'deleted';
  return customer.is_active ? 'active' : 'inactive';
};

export function CustomerStatusChip({ t, customer, sx }) {
  const status = customerStatus(customer);
  return <SoftChip size="small" label={t(`customers.statuses.${status}`)} color={STATUS_COLOR[status]} sx={sx} />;
}


// Row actions shared by the DataGrid kebab and the mobile list. Edit/Delete/
// Restore are internal-only (the backend enforces it too — IsInternalUser).
export const buildCustomerActions = ({ t, customer, isInternal, onView, onEdit, onDelete, onRestore }) => {
  const actions = [
    {
      label: t('customers.actions.view'),
      icon: <VisibilityOutlinedIcon fontSize="small" />,
      onClick: () => onView(customer),
    },
  ];
  if (!isInternal) return actions;
  if (customer.is_deleted) {
    actions.push({
      label: t('customers.actions.restore'),
      icon: <RestoreIcon fontSize="small" />,
      onClick: () => onRestore(customer),
    });
    return actions;
  }
  actions.push(
    {
      label: t('customers.actions.edit'),
      icon: <EditOutlinedIcon fontSize="small" />,
      onClick: () => onEdit(customer),
    },
    {
      label: t('customers.actions.delete'),
      icon: <DeleteOutlineIcon fontSize="small" />,
      color: 'error',
      onClick: () => onDelete(customer),
    },
  );
  return actions;
};

// With `getRowHeight="auto"` (needed so the two-line "Cliente" cell doesn't
// clip in compact density), DataGrid stops vertically centering cell content
// — wrapping single-line cells in this restores the centering.
function CenteredCell({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {children}
    </Box>
  );
}

export const buildCustomerColumns = ({ t, language, ...actionHandlers }) => [
  {
    field: 'display_name',
    headerName: t('customers.fields.displayName'),
    flex: 1,
    minWidth: 220,
    resizable: false,
    renderCell: (params) => (
      <Stack spacing={0} sx={{ py: 1 }}>
        <Link component={RouterLink} to={`/customers/${params.row.uuid}`} variant="body2" underline="hover">
          {params.row.display_name}
        </Link>
        {params.row.persona?.legal_name && (
          <Typography variant="caption" color="text.secondary">
            {params.row.persona.legal_name}
          </Typography>
        )}
      </Stack>
    ),
  },
  {
    field: 'tax_id',
    headerName: t('customers.fields.taxId'),
    width: 160,
    valueGetter: (value, row) => row.persona?.tax_id || '',
    renderCell: (params) => (
      <CenteredCell>{formatTaxId(params.value, params.row.persona?.country) || '—'}</CenteredCell>
    ),
  },
  {
    field: 'provider_name',
    headerName: t('customers.fields.provider'),
    width: 150,
    renderCell: (params) => <CenteredCell>{params.value}</CenteredCell>,
  },
  {
    field: 'businesses_count',
    headerName: t('customers.fields.businesses'),
    width: 120,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => (
      <CenteredCell>
        <Chip size="small" label={t('customers.list.businessesCount', { count: params.value ?? 0 })} />
      </CenteredCell>
    ),
  },
  {
    field: 'status',
    headerName: t('customers.fields.status'),
    width: 120,
    valueGetter: (value, row) => t(`customers.statuses.${customerStatus(row)}`),
    renderCell: (params) => (
      <CenteredCell>
        <CustomerStatusChip t={t} customer={params.row} />
      </CenteredCell>
    ),
  },
  {
    field: 'created_at',
    headerName: t('customers.fields.createdAt'),
    width: 130,
    type: 'dateTime',
    valueGetter: (value) => (value ? new Date(value) : null),
    renderCell: (params) => <CenteredCell>{formatDate(params.row.created_at, language)}</CenteredCell>,
  },
  {
    field: 'rowActions',
    headerName: '',
    width: 56,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: 'center',
    renderCell: (params) => (
      <CenteredCell>
        <RowActionsMenu actions={buildCustomerActions({ t, customer: params.row, ...actionHandlers })} />
      </CenteredCell>
    ),
  },
];
